import {
  Box,
  Button,
  InputGroup as ChakraInputGroup,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  InputRightElement,
  Radio,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { Insurer, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { Controller, useFormContext } from 'BrokerTsLib/src/rhf'
import { IsOtherNonregistered, IsTrailer } from 'BrokerTsLib/src/validation'
import { useEffect, useMemo } from 'react'

import { FormLabel } from '@/components/FormLabel'
import { NumberInput } from '@/components/ui/NumberInput'
import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { RadioGroup as CustomRadioGroup } from '@/containers/forms/RadioGroup'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { usePartner } from '@/context/PartnerContext'
import { Apps } from '@/dtos'
import { useInsurers, useSelections, useYearKm } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { LabelHintIcon } from '@/images/LabelHintIcon'
import { formatDate } from '@/utils/formatDate'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import { VehiclePriceSelection } from '../../vehicle/Step1/forms/VehicleForm/VehiclePriceSelection'
import { CarDetails } from '../../vehicle/Step1/forms/VehicleForm/components/CarDetails'

export const VehicleForm = () => {

  const { app } = useApp()
  const isPublicPoleas = app == Apps.publicPoleas
  const { currentPartner } = usePartner()
  const { insuranceParamsId, isDisabled } = useInsuranceParams()

  const methods = useFormContext<InsuranceParamsDto>()
  const {
    formState: { errors },
    register,
    getValues,
    setValue,
    watch,
    control,
  } = methods

  const yearKm = watch('vehicle.yearKm')

  const isJapPoleasing = !!getValues('importedData')
  const category = watch('vehicle.druh')
  const manufacturerWarranty = watch('vehicle.manufacturerWarrantyType')
  const smluvniServis = watch('vehicle.smluvniServis')

  const { data: dataVehicleYearKm } = useYearKm(category ?? 0, { enabled: !!category })
  const { data: dataPalivo } = useSelections(SelectionType.Palivo)
  const { data: dataPuvod } = useSelections(SelectionType.PuvodVozidla)
  const { data: dataZabezpeceni, isLoading: isLoadingZabezpeceni, isFetching: isFetchingZabezpeceni } = useSelections(SelectionType.Zabezpeceni)
  const { data: dataDruh } = useSelections(SelectionType.DruhVozidla)
  const { data: dataWarrantyLimitKm, isLoading: isLoadingWarrantyLimitKm, isFetching: isFetchingWarrantyLimitKm } = useSelections(SelectionType.WarrantyLimitKm)
  const { data: dataWarrantyMonthLimit, isLoading: isLoadingWarrantyMonthLimit, isFetching: isFetchingWarrantyMonthLimit } = useSelections(SelectionType.WarrantyMonthLimit)

  const { data: allInsurers } = useInsurers(false, false)
  const { data: allAddonInsurers } = useInsurers(true, false)

  const addonInsurers = useMemo(() => allAddonInsurers?.data.map(x => x.id), [allAddonInsurers]) ?? []

  const isPrivesNaves = IsTrailer(category?.toString())
  const isOtherNonregistered = IsOtherNonregistered(category?.toString())
  useEffect(() => {
    if (isOtherNonregistered)
      setValue('vehicle.isInProduction', false)
  }, [isOtherNonregistered])

  // nove umoznujeme pojistovat i jine pojistovny nez pouze subaru insurance. Proto nic na formulari neskryvam. V pripade reklamaci lze vratit zpet
  const hidePartsForSubaruInsurance = false

  return (
    <>
      <FormBody>
        <CarDetails
          showInsertButton={false}
          vehicle={{
            CategoryID: getValues('vehicle.druh') ?? undefined,
            MakeBase: getValues('vehicle.znacka') ?? undefined,
            ModelText: getValues('vehicle.model') ?? undefined,
            vin: getValues('vehicle.vin') ?? undefined,
            registrationMark: getValues('vehicle.licensePlate') ?? undefined,
            TechnicalCertificateNumber: getValues('vehicle.cisloTechnickehoPrukazu') ?? undefined,
            puvodVozidla: dataPuvod?.data.find(x => x.id == getValues('vehicle.puvod'))?.description ?? undefined,
            CylinderVolume: getValues('vehicle.objem') ?? undefined,
            EnginePower: getValues('vehicle.vykon1') ?? undefined,
            SeatsCount: getValues('vehicle.pocetMistSezeni') ?? undefined,
            TotalWeigth: getValues('vehicle.celkovaHmotnost') ?? undefined,
            FirstRegistrationDate: getValues('vehicle.firstRegistrationDate') ? getValues('vehicle.firstRegistrationDate') ?? undefined : undefined,
            FirstRegistrationDateString: getValues('vehicle.firstRegistrationDate') ? formatDate(getValues('vehicle.firstRegistrationDate') ?? undefined) : undefined,
            // Verze: dataDruh?.data.find(x => x.id == getValues('vehicle.druh'))?.description ?? undefined, // davam do verze, aby se to zobrazilo v prvnim radku vedle modelu
            druhPaliva: dataPalivo?.data.find(x => x.id == getValues('vehicle.palivo'))?.description ?? undefined,
          }}
        />

        <FormRow >
          {!(isPrivesNaves || isOtherNonregistered) &&
            <FormControl isInvalid={!!errors.vehicle?.mileage} isRequired>
              <FormLabel>
                Stav tachometru
              </FormLabel>

              <ChakraInputGroup>
                <NumberInput
                  placeholder="Např. 150 000"
                  {...register('vehicle.mileage')}
                  isDisabled={isDisabled}
                />
                <InputRightElement>km</InputRightElement>
              </ChakraInputGroup>

              {currentPartner?.partner == 'Respect' &&
                <FormHelperText>
                  <Button as={'a'} target='_blank' href={'https://kontrolatachometru.cz/'} variant='link'>Km</Button>
                </FormHelperText>
              }

              {errors.vehicle?.mileage && <FormErrorMessage>{errors.vehicle.mileage.message}</FormErrorMessage>}

            </FormControl>
          }

          {!hidePartsForSubaruInsurance && !isOtherNonregistered &&
            (dataVehicleYearKm?.data.length ?? 0) > 0 &&
            <FormControl isInvalid={!!errors.vehicle?.yearKm} isRequired={!isJapPoleasing}>
              <Stack direction={'row'}>
                <FormLabel>Roční nájezd</FormLabel>
              </Stack>

              {(dataVehicleYearKm?.data.length ?? 0) > 0 &&
                <Select {...register('vehicle.yearKm')} value={watch('vehicle.yearKm') ?? ''} isDisabled={isDisabled}>
                  <option disabled={!isJapPoleasing} value=''>Vyberte roční nájezd</option>
                  {dataVehicleYearKm?.data.map((item) => (
                    <option value={item.value?.toString() ?? ''} key={item.value}>{item.name}</option>
                  ))}
                </Select>
              }

              {errors.vehicle?.yearKm && <FormErrorMessage>{errors.vehicle.yearKm.message}</FormErrorMessage>}

              <FormHelperText>
                Vyberte předpokládaný roční nájezd vozidla. Pokud údaj neznáte vyberte možnost {currentPartner?.partner == 'Jap' ? '"nemám zájem"' : '"nechci uvést"'}.
              </FormHelperText>
            </FormControl>
          }
        </FormRow>

        {!isOtherNonregistered &&
          addonInsurers.findIndex(x => x == Insurer.Fortegra || x == Insurer.Colonnade) >= 0 &&
          <>
            <Flex>
              <FormControl isInvalid={!!errors.vehicle?.manufacturerWarrantyType} isRequired={isPublicPoleas}>
                <Stack direction={'row'}>
                  <FormLabel tooltip={'Pouze pro kalkulace doplňkového pojištění prodloužené záruky.'}>Vozidlo v záruce</FormLabel>
                </Stack>

                <CustomRadioGroup
                  name="vehicle.manufacturerWarrantyType"
                  valueTransform={(value) => value?.toString() ?? '0'}
                  isDisabled={isDisabled}
                  onChange={(e) => {
                    setValue('vehicle.manufacturerWarrantyType', parseInt(e))

                    if (e == '1') {
                      if (!getValues('vehicle.warrantyStartDate'))
                        setValue('vehicle.warrantyStartDate', getValues('vehicle.firstRegistrationDate'))
                    }
                  }}>
                  <Radio value={'1'}>Ano</Radio>
                  <Radio value={'2'}>Ne</Radio>
                  <Radio value={'0'}>Nemám zájem</Radio>
                </CustomRadioGroup>

                {errors.vehicle?.manufacturerWarrantyType && <FormErrorMessage>{errors.vehicle.manufacturerWarrantyType.message}</FormErrorMessage>}
              </FormControl>
            </Flex>

            {manufacturerWarranty == 0 && !isDisabled &&
              <Text variant="warning">Připojištění prodloužené záruky nebude kalkulováno</Text>
            }

            {manufacturerWarranty == 1 &&
              <>
                <FormRow itemsCount={3}>
                  <FormControl isInvalid={!!errors.vehicle?.warrantyStartDate} isRequired>
                    <FormLabel htmlFor="vehicle.warrantyStartDate">
                      Záruka od
                    </FormLabel>

                    <Datepicker
                      isDisabled={isDisabled}
                      name="vehicle.warrantyStartDate"
                    />
                  </FormControl>

                  <FormControl isInvalid={!!errors.vehicle?.warrantyTermLimit} isRequired>
                    <FormLabel htmlFor="vehicle.warrantyTermLimit">
                      Počet měsíců záruky
                    </FormLabel>
                    {(isFetchingWarrantyMonthLimit || isLoadingWarrantyMonthLimit) && <Spinner />}

                    {(dataWarrantyMonthLimit?.data.length ?? 0) > 0 &&
                      <Select id="vehicle.warrantyTermLimit" {...register('vehicle.warrantyTermLimit')} isDisabled={isDisabled}>
                        <option value='' disabled>Vyberte</option>
                        {dataWarrantyMonthLimit?.data.
                          sort((x, y) => (x.displayOrder ?? x.id) - (y.displayOrder ?? y.id)).
                          map((item) => (
                            <option value={item.value} key={item.id}>{item.description}</option>
                          ))}
                      </Select>
                    }
                  </FormControl>

                  <FormControl isInvalid={!!errors.vehicle?.warrantyKmLimit} isRequired>
                    <FormLabel htmlFor="vehicle.warrantyKmLimit">
                      Počet km záruky
                    </FormLabel>
                    {(isFetchingWarrantyLimitKm || isLoadingWarrantyLimitKm) && <Spinner />}

                    {(dataWarrantyLimitKm?.data.length ?? 0) > 0 &&
                      <Select id="vehicle.warrantyKmLimit" {...register('vehicle.warrantyKmLimit')} isDisabled={isDisabled}>
                        <option value='' disabled>Vyberte</option>
                        {dataWarrantyLimitKm?.data
                          .sort((x, y) => (x.displayOrder ?? x.id) - (y.displayOrder ?? y.id))
                          .filter(item => item.name != 'NU')
                          .map((item) => (
                            <option value={item.value} key={item.id}>{item.description}</option>
                          ))}
                      </Select>
                    }
                  </FormControl>
                </FormRow>
              </>
            }
          </>
        }

        {!isOtherNonregistered &&
          <>
            <SimpleGrid columns={{ sm: 1, md: 1 }} spacingX='10' spacingY='4' w='100%'>
              <FormControl isInvalid={!!errors.vehicle?.smluvniServis}>
                <Flex gap={2} alignItems='center'>
                  <Checkbox
                    isChecked={smluvniServis ?? false}
                    isDisabled={isDisabled}
                    onChange={(e) => setValue('vehicle.smluvniServis', e.target.checked)}>
                    Souhlas s opravou ve sml. servisu
                  </Checkbox>

                  <CustomTooltip
                    hasArrow
                    label='Zaškrtnutím souhlasíte s opravou ve smluvním servisu pojistitele, za což dostáváte slevu na pojistném. V případě porušení závazku opravovat ve smluvním servise může pojistitel krátit pojistné plnění nebo navýšit spoluúčast.'
                    placement="top"
                    trigger='clickHover'
                    maxW={'400px'}
                  >
                    <Box sx={{ lineHeight: 0 }}>
                      <LabelHintIcon width={4} height={4} />
                    </Box>
                  </CustomTooltip>
                </Flex>
              </FormControl>
            </SimpleGrid>

            <FormControl w='100%' isInvalid={!!errors.vehicle?.security}>
              <Stack direction={'row'}>
                <FormLabel htmlFor="vehicle.security">Zabezpečení</FormLabel>
                {(isFetchingZabezpeceni || isLoadingZabezpeceni) && <Spinner />}
              </Stack>

              <Controller
                name="vehicle.security"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CheckboxGroup onChange={values => onChange(values.map(val => parseInt(val.toString())))} value={value ?? []} isDisabled={isDisabled}>
                    <SimpleGrid columns={{ sm: 2, md: 3 }} spacingX='10' spacingY='4' w='100%'>
                      {dataZabezpeceni?.data
                        .filter(x => [
                          Selection.GlassMarking,
                          Selection.PassiveSearch,
                          Selection.ActiveSearch,
                          Selection.GearLevelLock
                        ].includes(x.id as any))
                        .map((item) => (
                          <Checkbox value={item.id} key={item.id}>
                            {item.description}
                          </Checkbox>
                        ))}
                    </SimpleGrid>
                  </CheckboxGroup>
                )}
              />
            </FormControl>
          </>
        }

        <VehiclePriceSelection
          isDisabled={isDisabled}
          extraWizard={false}
          insuranceParamsId={insuranceParamsId}
        />

      </FormBody >
    </>
  )
}

